import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../hooks';
import './FeedbackBubble.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const REPORT_TYPES = [
  { key: 'bug', icon: 'fa-bug' },
  { key: 'inappropriate', icon: 'fa-exclamation-triangle' },
  { key: 'suggestion', icon: 'fa-lightbulb' },
  { key: 'feedback', icon: 'fa-comment-dots' }
];

const COOLDOWN_DAYS = 30;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
const LS_KEY = 'feedback_bounce_seen';

const FeedbackBubble = ({ hidden, user }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [showBounce, setShowBounce] = useState(false);

  const successTimerRef = useRef(null);
  const wrapperRef = useClickOutside(() => {
    if (isOpen) setIsOpen(false);
  });

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Smart trigger: check if should show badge after 3+ games
  useEffect(() => {
    if (!user || !user.stats || user.stats.gamesPlayed < 3) return;

    const lastSeen = localStorage.getItem(LS_KEY);
    if (lastSeen) {
      const elapsed = Date.now() - new Date(lastSeen).getTime();
      if (elapsed < COOLDOWN_MS) return;
    }

    // Check API for last feedback
    fetch(`${API_URL}/api/reports/my/last-feedback`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
        if (data.lastFeedbackAt) {
          const elapsed = Date.now() - new Date(data.lastFeedbackAt).getTime();
          if (elapsed < COOLDOWN_MS) return;
        }
        setShowBadge(true);
        setShowBounce(true);
        // Remove bounce after animation
        setTimeout(() => setShowBounce(false), 1200);
      })
      .catch(() => {});
  }, [user]);

  // Clear success timer on unmount or manual close
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const resetForm = useCallback(() => {
    setSelectedType(null);
    setTitle('');
    setDescription('');
    setError('');
    setSuccess(false);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
  }, []);

  const handleOpen = () => {
    if (!isOpen) {
      resetForm();
      setShowBadge(false);
      localStorage.setItem(LS_KEY, new Date().toISOString());
    }
    setIsOpen(!isOpen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title || title.length < 5) {
      setError(t('report.errors.titleMinLength', 'Title must be at least 5 characters'));
      return;
    }
    if (!description || description.length < 10) {
      setError(t('report.errors.descriptionMinLength', 'Description must be at least 10 characters'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: selectedType,
          title,
          description
        })
      });

      if (!res.ok) throw new Error('Submit failed');

      setSuccess(true);
      localStorage.setItem(LS_KEY, new Date().toISOString());

      successTimerRef.current = setTimeout(() => {
        setIsOpen(false);
        resetForm();
      }, 3000);
    } catch {
      setError(t('report.errors.submitFailed', 'Failed to submit report'));
    } finally {
      setSubmitting(false);
    }
  };

  if (hidden) return null;

  const getTypeI18nKey = (type) => type.i18nKey || type.key;

  return (
    <div className="feedback-bubble-wrapper" ref={wrapperRef}>
      {isOpen && (
        <div className="feedback-panel" role="dialog" aria-label={t('feedback.ariaLabel', 'Feedback')}>
          <div className="feedback-panel-header">
            {selectedType && !success ? (
              <button className="feedback-panel-back" onClick={() => setSelectedType(null)}>
                <i className="fas fa-arrow-left"></i> {t('feedback.back', 'Back')}
              </button>
            ) : (
              <h3>{t('feedback.panelTitle', 'Send Feedback')}</h3>
            )}
            <button
              className="feedback-panel-close"
              onClick={() => setIsOpen(false)}
              aria-label={t('common.close', 'Close')}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Login prompt for unauthenticated users */}
          {!user && (
            <div className="feedback-login-prompt">
              <i className="fas fa-user-lock"></i>
              <p>{t('feedback.loginRequired', 'You need to be logged in to send feedback.')}</p>
              <a href={`${API_URL}/auth/battlenet`}>
                {t('feedback.loginButton', 'Log in with Battle.net')}
              </a>
            </div>
          )}

          {/* Success state */}
          {user && success && (
            <div className="feedback-success">
              <i className="fas fa-check-circle"></i>
              <p>{t('report.success.message', 'Thank you for your report. We will review it as soon as possible.')}</p>
            </div>
          )}

          {/* Type selection grid */}
          {user && !success && !selectedType && (
            <div className="feedback-type-grid">
              {REPORT_TYPES.map((type) => (
                <button
                  key={type.key}
                  className="feedback-type-btn"
                  onClick={() => setSelectedType(type.key)}
                >
                  <i className={`fas ${type.icon}`}></i>
                  <span>{t(`report.types.${getTypeI18nKey(type)}`, type.key)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Form */}
          {user && !success && selectedType && (
            <form className="feedback-form" onSubmit={handleSubmit}>
              <div className="feedback-form-group">
                <label>{t('report.form.titleLabel', 'Title')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('report.form.titlePlaceholder', 'Brief description of the issue')}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div className="feedback-form-group">
                <label>{t('report.form.descriptionLabel', 'Description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('report.form.descriptionPlaceholder', 'Explain the issue in detail...')}
                  maxLength={2000}
                />
              </div>
              {error && <div className="feedback-form-error">{error}</div>}
              <button
                type="submit"
                className="feedback-form-submit"
                disabled={submitting}
              >
                {submitting
                  ? t('report.form.submitting', 'Sending...')
                  : t('report.form.submit', 'Send Report')
                }
              </button>
            </form>
          )}
        </div>
      )}

      <button
        className={`feedback-bubble${showBounce ? ' bounce' : ''}`}
        onClick={handleOpen}
        aria-label={t('feedback.ariaLabel', 'Feedback')}
        title={t('feedback.ariaLabel', 'Feedback')}
      >
        <i className={`fas ${isOpen ? 'fa-times' : 'fa-comment-dots'}`}></i>
        {showBadge && !isOpen && <span className="feedback-bubble-badge"></span>}
      </button>
    </div>
  );
};

export default FeedbackBubble;
